/**
 * Order type of a booking: 'standard' (normal talent booking), 'kind' (KIND / CGI order)
 * or 'copywrite' (Copywrite order). The backend stores it in `bookings.order_type`.
 *
 * Bookings created before the column existed (and older API responses) may not carry it,
 * so `getOrderType` falls back to the set name the app used to recognise KIND orders by.
 * Keep that fallback here and nowhere else — components should only ever ask the helper.
 */
export const ORDER_TYPES = {
  STANDARD: 'standard',
  KIND: 'kind',
  COPYWRITE: 'copywrite',
};

const VALID_TYPES = Object.values(ORDER_TYPES);

const KIND_SETS = ['digital production', 'digital_production'];
// 'copywrite' = orders created before 2026-09-22, 'copywriting' = since then (same word as the talents)
const COPYWRITE_SETS = ['copywrite', 'copywriting'];

const setName = (booking) => {
  const set = booking?.set;
  if (typeof set === 'string') return set.trim().toLowerCase();
  if (set && typeof set === 'object') {
    const v = set.value ?? set.label ?? set.name;
    return typeof v === 'string' ? v.trim().toLowerCase() : '';
  }
  return '';
};

export const getOrderType = (booking) => {
  if (!booking) return ORDER_TYPES.STANDARD;
  const explicit = typeof booking.order_type === 'string' ? booking.order_type.toLowerCase() : null;
  if (explicit && VALID_TYPES.includes(explicit)) return explicit;

  const set = setName(booking);
  if (KIND_SETS.includes(set)) return ORDER_TYPES.KIND;
  if (COPYWRITE_SETS.includes(set)) return ORDER_TYPES.COPYWRITE;
  return ORDER_TYPES.STANDARD;
};

export const isKindBooking = (booking) => getOrderType(booking) === ORDER_TYPES.KIND;
export const isCopywriteBooking = (booking) => getOrderType(booking) === ORDER_TYPES.COPYWRITE;
/** KIND or Copywrite — both store their extra info in `kind_info`. */
export const isTypedBooking = (booking) => {
  const type = getOrderType(booking);
  return type === ORDER_TYPES.KIND || type === ORDER_TYPES.COPYWRITE;
};

/** Display name of an order type, e.g. for the title prefix. */
export const orderTypeLabel = (type) => {
  switch (type) {
    case ORDER_TYPES.KIND: return 'KIND';
    case ORDER_TYPES.COPYWRITE: return 'Copywrite';
    default: return 'Standard';
  }
};

/**
 * Group label as shown in the lists. KIND / digital orders are grouped as "kind-197" in the
 * database; since 2026-10-29 (Ariane) they read "digital production - 197" in the UI. The stored
 * value is untouched so filters, URLs and group actions keep working.
 */
export const formatGroupLabel = (group) => {
  if (typeof group !== 'string') return group || '';
  const m = group.match(/^kind-(\d+)$/i);
  return m ? `digital production - ${m[1]}` : group;
};

/** Heading for the `kind_info` block; null for standard bookings that have none. */
export const orderTypeInfoLabel = (type) => {
  switch (type) {
    case ORDER_TYPES.KIND: return 'Digital Info';
    case ORDER_TYPES.COPYWRITE: return 'Copywrite Info';
    default: return null;
  }
};
